import { useState, useEffect } from 'react';
import { toDateLocal } from '../../utils/dateUtils';
import { CAUSE_LABELS } from './SleepInterruptionModal';

const CAUSE_OPTIONS = Object.entries(CAUSE_LABELS);

function blankWakeUp() {
  return { time: new Date().toISOString(), cause: 'dummy', wentBackToSleep: true, notes: '' };
}

/** Format ISO timestamp to HH:MM (24h) for the wake-up list. */
function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/**
 * SleepEditModal — custom edit modal for sleep entries.
 *
 * Handles the standard sleep fields (date, start/end time, type, notes) plus
 * a Wake-ups panel where existing entries can be removed and new ones added.
 * Replaces the generic EditModal for the sleepTable.
 */
export default function SleepEditModal({ isOpen, item, onSave, onCancel, isLoading }) {
  const [date, setDate]           = useState('');
  const [startTime, setStart]     = useState('');
  const [endTime, setEnd]         = useState('');
  const [type, setType]           = useState('nap');
  const [notes, setNotes]         = useState('');
  const [wakeUps, setWakeUps]     = useState([]);
  const [addingWakeUp, setAdding] = useState(false);
  const [newWakeUp, setNew]       = useState(blankWakeUp);

  // Sync form state whenever the item being edited changes
  useEffect(() => {
    if (!isOpen || !item) return;
    const raw = item.date ?? item.createdAt ?? '';
    const dateStr = raw
      ? (typeof raw === 'string' ? raw.split('T')[0] : new Date(raw).toISOString().split('T')[0])
      : toDateLocal();
    setDate(dateStr);
    setStart(item.startTime || '');
    setEnd(item.endTime || '');
    setType(item.type || 'nap');
    setNotes(item.notes || '');
    setWakeUps(Array.isArray(item.wakeUps) ? [...item.wakeUps] : []);
    setAdding(false);
    setNew(blankWakeUp());
  }, [isOpen, item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen || !item) return null;

  const handleSave = (e) => {
    e.preventDefault();
    onSave({
      date:        new Date(`${date}T${startTime}:00`).toISOString(),
      startTime,
      endTime:     endTime || null,
      type,
      notes,
      wakeUps,
      wakeUpCount: wakeUps.length,
    });
  };

  const removeWakeUp = (idx) => setWakeUps(prev => prev.filter((_, i) => i !== idx));

  const confirmAdd = () => {
    setWakeUps(prev => [...prev, { ...newWakeUp }]);
    setNew(blankWakeUp());
    setAdding(false);
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
      <div className="modal-dialog" style={{ maxWidth: 580 }}>
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">Edit Sleep Entry</h5>
            <button type="button" className="btn-close btn-close-white" onClick={onCancel} disabled={isLoading} />
          </div>

          <form onSubmit={handleSave}>
            <div className="modal-body">

              {/* ── Standard fields ─────────────────────────────────── */}
              <div className="row g-2 mb-3">
                <div className="col-6 col-md-3">
                  <label className="form-label">Date</label>
                  <input type="date" className="form-control" value={date}
                    onChange={e => setDate(e.target.value)} required disabled={isLoading} />
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label">Start Time</label>
                  <input type="time" className="form-control" value={startTime}
                    onChange={e => setStart(e.target.value)} required disabled={isLoading} />
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label">End Time</label>
                  <input type="time" className="form-control" value={endTime}
                    onChange={e => setEnd(e.target.value)} disabled={isLoading} />
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label">Type</label>
                  <select className="form-select" value={type}
                    onChange={e => setType(e.target.value)} disabled={isLoading}>
                    <option value="nap">Nap</option>
                    <option value="night">Night Sleep</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="form-label">Notes</label>
                <input type="text" className="form-control" placeholder="Optional"
                  value={notes} onChange={e => setNotes(e.target.value)} disabled={isLoading} />
              </div>

              {/* ── Wake-ups ─────────────────────────────────────────── */}
              <div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <p className="mb-0 fw-semibold" style={{ fontSize: '0.875rem' }}>
                    😴 Wake-ups ({wakeUps.length})
                  </p>
                  {!addingWakeUp && (
                    <button type="button" className="btn btn-sm btn-outline-primary"
                      onClick={() => setAdding(true)}>
                      + Add wake-up
                    </button>
                  )}
                </div>

                {wakeUps.length === 0 && !addingWakeUp && (
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>
                    No wake-ups logged for this session.
                  </p>
                )}

                <ul className="wakeup-list mb-2">
                  {wakeUps.map((w, i) => (
                    <li key={i} className="wakeup-list-item">
                      <span className="wakeup-time">{fmtTime(w.time)}</span>
                      <span className="wakeup-cause">{CAUSE_LABELS[w.cause] ?? w.cause}</span>
                      <span className="wakeup-meta">
                        · {w.wentBackToSleep ? 'back to sleep ✓' : 'stayed awake'}
                        {w.notes ? ` · ${w.notes}` : ''}
                      </span>
                      <button type="button" className="wakeup-remove-btn"
                        onClick={() => removeWakeUp(i)} title="Remove">✕</button>
                    </li>
                  ))}
                </ul>

                {/* Inline add-wake-up form */}
                {addingWakeUp && (
                  <div className="p-2 rounded mb-2"
                    style={{ background: 'var(--bg-light)', border: '1px solid var(--border-light)' }}>
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <label className="form-label">Cause</label>
                        <select className="form-select form-select-sm"
                          value={newWakeUp.cause}
                          onChange={e => setNew(p => ({ ...p, cause: e.target.value }))}>
                          {CAUSE_OPTIONS.map(([val, lbl]) => (
                            <option key={val} value={val}>{lbl}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="form-label">Back to sleep?</label>
                        <select className="form-select form-select-sm"
                          value={newWakeUp.wentBackToSleep ? 'yes' : 'no'}
                          onChange={e => setNew(p => ({ ...p, wentBackToSleep: e.target.value === 'yes' }))}>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                    </div>
                    <input type="text" className="form-control form-control-sm mb-2"
                      placeholder="Note (optional)"
                      value={newWakeUp.notes}
                      onChange={e => setNew(p => ({ ...p, notes: e.target.value }))} />
                    <div className="d-flex gap-2">
                      <button type="button" className="btn btn-sm btn-primary" onClick={confirmAdd}>
                        Add
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-secondary"
                        onClick={() => { setAdding(false); setNew(blankWakeUp()); }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
